import { Alert, Box, Button, Stack } from '@mui/material'
import { useDownloadReport, useRetryMultipleTransactions, useRetrySingleTransaction } from '@src/api/transaction-retry-api'
import { useSelectedCompany } from '@src/providers/SelectedCompanyProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FailedTransactionsList from './FailedTransactionsList'
import { useFailedTransactionsData, useTransactionRetrySelection } from './TransactionRetry-hooks'

interface RetryError extends Error {
  details?: {
    succeeded: string[]
    failed: { fileName: string; error: unknown }[]
  }
}

const TransactionRetryMain = () => {
  const { t } = useTranslation()
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const selectedCompany = useSelectedCompany()
  const companyId = selectedCompany ? String(selectedCompany) : undefined

  // Hooks for data (without filters)
  const { transactions, isLoading, error } = useFailedTransactionsData()

  // Hooks for selection
  const { selectedIds, clearSelection, setSelectedIds } = useTransactionRetrySelection(transactions)

  // Hooks for actions - using new direct download instead of SAS token
  const retryMultiple = useRetryMultipleTransactions(companyId)
  const retrySingle = useRetrySingleTransaction(companyId)
  const downloadReport = useDownloadReport(companyId)

  const handleSelectionChange = useCallback(
    (newSelectedIds: string[]) => {
      setSelectedIds(newSelectedIds)
    },
    [setSelectedIds],
  )

  const handleViewReport = useCallback(
    async (fileName: string) => {
      try {
        setMessage({ type: 'success', text: t('admin.transactionRetry.messages.downloadingReport', { fileName }) })

        // Directly download the file using the new API
        await downloadReport.mutateAsync(fileName)

        setMessage({ type: 'success', text: t('admin.transactionRetry.messages.downloadSuccess', { fileName }) })
      } catch (error) {
        reportErrorToConsole(error)
        setMessage({ type: 'error', text: t('admin.transactionRetry.errors.downloadError', { fileName }) })
      }
    },
    [t, downloadReport],
  )

  const handleRetryProcessing = useCallback(
    async (fileName: string) => {
      try {
        await retrySingle.mutateAsync(fileName)
        setMessage({ type: 'success', text: t('admin.transactionRetry.messages.relaunchSuccess', { fileName }) })
      } catch (_error) {
        setMessage({ type: 'error', text: t('admin.transactionRetry.errors.relaunchError', { fileName }) })
      }
    },
    [retrySingle, t],
  )

  const handleRetryMultiple = useCallback(
    async (fileNames: string[]) => {
      try {
        await retryMultiple.mutateAsync(fileNames)
        setMessage({
          type: 'success',
          text: t('admin.transactionRetry.messages.relaunchMultipleSuccess', { count: fileNames.length }),
        })
        clearSelection()
      } catch (error) {
        // V�rifier si l'erreur contient des d�tails sur les �checs
        const retryError = error as RetryError
        if (retryError.details?.failed && retryError.details.failed.length > 0) {
          const failedCount = retryError.details.failed.length
          const succeededCount = retryError.details.succeeded.length
          setMessage({
            type: 'error',
            text: t('admin.transactionRetry.errors.relaunchMultiplePartialError', {
              failed: failedCount,
              succeeded: succeededCount,
              total: failedCount + succeededCount,
            }),
          })
        } else {
          setMessage({ type: 'error', text: t('admin.transactionRetry.errors.relaunchMultipleError') })
        }
      }
    },
    [retryMultiple, clearSelection, t],
  )

  const handleRetrySelected = useCallback(async () => {
    if (selectedIds.size === 0) {
      setMessage({ type: 'error', text: t('admin.transactionRetry.errors.noTransactionSelected') })
      return
    }

    try {
      // Trouver les transactions correspondant aux IDs s�lectionn�s et convertir en fichiers JSON
      const selectedTransactionsForRetry = transactions.filter((transaction) => selectedIds.has(transaction.id))

      const jsonFileNames = selectedTransactionsForRetry.map((transaction) =>
        transaction.fileName.replace('.csv', '.json'),
      )

      await retryMultiple.mutateAsync(jsonFileNames)
      setMessage({
        type: 'success',
        text: t('admin.transactionRetry.messages.retrySuccess', { count: selectedTransactionsForRetry.length }),
      })
      clearSelection()
    } catch (error) {
      // V�rifier si l'erreur contient des d�tails sur les �checs
      const retryError = error as RetryError
      if (retryError.details?.failed && retryError.details.failed.length > 0) {
        const failedCount = retryError.details.failed.length
        const succeededCount = retryError.details.succeeded.length
        setMessage({
          type: 'error',
          text: t('admin.transactionRetry.errors.retryPartialError', {
            failed: failedCount,
            succeeded: succeededCount,
            total: failedCount + succeededCount,
          }),
        })
      } else {
        setMessage({
          type: 'error',
          text: t('admin.transactionRetry.errors.retryFailed'),
        })
      }
    }
  }, [selectedIds, transactions, retryMultiple, clearSelection, t])

  return (
    <Box sx={{ p: 3 }}>
      {/* Action Bar */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}
      >
        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          color="primary"
          onClick={() => void handleRetrySelected()}
          disabled={selectedIds.size === 0 || retryMultiple.isPending}
        >
          {retryMultiple.isPending
            ? t('admin.transactionRetry.actions.retryInProgress')
            : t('admin.transactionRetry.actions.retry', { count: selectedIds.size })}
        </Button>
      </Stack>

      {/* Messages */}
      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('admin.transactionRetry.errors.loadingFailed')}:{' '}
          {error instanceof Error ? error.message : t('admin.transactionRetry.errors.unknown')}
        </Alert>
      )}

      {retryMultiple.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('admin.transactionRetry.errors.retryError')}:{' '}
          {retryMultiple.error instanceof Error
            ? retryMultiple.error.message
            : t('admin.transactionRetry.errors.unknown')}
        </Alert>
      )}

      {/* Transactions List */}
      <FailedTransactionsList
        transactions={transactions}
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
        onViewReport={handleViewReport}
        onRetryProcessing={handleRetryProcessing}
        onRetryMultiple={handleRetryMultiple}
        isLoading={isLoading}
      />
    </Box>
  )
}

export default TransactionRetryMain